// Title-logo intake (design ADR-0013; copy + checks from jvm
// notes/biz/kb/kb_artwork_guide_v1.md Part 2). The series' title treatment is
// a 1000 x 400 transparent PNG the shells draw over the hero art in place of
// the typed title. Every check runs client-side on the decoded bitmap so the
// message arrives with the exact numbers BEFORE the upload; the fit-and-pad
// step delivers the exact canvas (scaled up or down, aspect kept) so the
// server's cover-crop is a no-op (plan 2.7: a 4:1 logo would otherwise lose
// its sides silently). Pixel math is pure and unit-tested; canvas/File
// plumbing is at the bottom and stays thin.

export const TITLE_LOGO_W = 1000;
export const TITLE_LOGO_H = 400;
/** Below this share of transparent pixels the "logo" is a photo or a box behind text. */
export const TITLE_LOGO_MIN_TRANSPARENT_RATIO = 0.3;
/** Opaque pixels' median luminance must read light on the dark hero ground. */
export const TITLE_LOGO_MIN_OPAQUE_MEDIAN_LUMINANCE = 0.6;
/** Aspect tolerance around 5:2 before the fit-and-pad note becomes a warning. */
export const TITLE_LOGO_ASPECT_TOLERANCE = 0.1;
/**
 * Smallest source the fit step accepts: the shells draw the delivered canvas
 * at most at 0.4 scale (the 500 x 160 FHD box), so anything below 400 x 160
 * would be enlarged past its native size ON THE TV; smaller than the canvas
 * still uploads, scaled up, with a softness warning.
 */
export const TITLE_LOGO_MIN_W = 400;
export const TITLE_LOGO_MIN_H = 160;
/** Alpha thresholds: <= TRANSPARENT counts as clear, >= OPAQUE as paint. */
const ALPHA_TRANSPARENT = 16;
const ALPHA_OPAQUE = 128;
/** Cap on sampled pixels — a 4000 x 1600 export is stats-identical at a stride. */
const MAX_SAMPLES = 400_000;

export interface TitleLogoPixelStats {
  /** Pixels sampled (not the image's total when a stride applies). */
  sampled: number;
  transparent: number;
  opaque: number;
  /** Any pixel with alpha < 255 — a PNG exported without transparency has none. */
  hasAlpha: boolean;
  /** Median relative luminance (0..1) of the opaque pixels; null when none. */
  opaqueMedianLuminance: number | null;
}

export interface TitleLogoVerdict {
  /** Stops the upload; the message carries the numbers the customer can act on. */
  block?: string;
  /** Shown once, the upload continues. */
  warn?: string;
}

export interface FitPadGeometry {
  /** Scale applied to the source: up or down, so the mark fills the canvas in one direction. */
  scale: number;
  drawW: number;
  drawH: number;
  /** Draw origin inside the 1000 x 400 canvas: left-aligned, bottom-anchored. */
  dx: number;
  dy: number;
}

export class TitleLogoUtils {
  /** Relative luminance (Rec. 709 weights) of an 8-bit RGB triple, 0..1. */
  static luminance(r: number, g: number, b: number): number {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  /**
   * Sampling stride so at most MAX_SAMPLES pixels are read; 1 for anything
   * up to 400k pixels (the 1000 x 400 target itself).
   */
  static sampleStride(pixelCount: number): number {
    return Math.max(1, Math.ceil(pixelCount / MAX_SAMPLES));
  }

  /** Transparency + brightness stats over RGBA data (every `stride`-th pixel). */
  static pixelStats(rgba: Uint8ClampedArray, stride = 1): TitleLogoPixelStats {
    const step = Math.max(1, Math.floor(stride)) * 4;
    let sampled = 0;
    let transparent = 0;
    let hasAlpha = false;
    const lums: number[] = [];
    for (let i = 0; i + 3 < rgba.length; i += step) {
      sampled++;
      const a = rgba[i + 3];
      if (a < 255) hasAlpha = true;
      if (a <= ALPHA_TRANSPARENT) {
        transparent++;
      } else if (a >= ALPHA_OPAQUE) {
        lums.push(TitleLogoUtils.luminance(rgba[i], rgba[i + 1], rgba[i + 2]));
      }
    }
    let opaqueMedianLuminance: number | null = null;
    if (lums.length > 0) {
      lums.sort((x, y) => x - y);
      const mid = Math.floor(lums.length / 2);
      opaqueMedianLuminance =
        lums.length % 2 === 1 ? lums[mid] : (lums[mid - 1] + lums[mid]) / 2;
    }
    return { sampled, transparent, opaque: lums.length, hasAlpha, opaqueMedianLuminance };
  }

  /**
   * PURE verdict for a decoded candidate. `isPng` comes from the file's type
   * or extension (the uploader already restricts the picker to PNG; this is
   * the belt to that suspender).
   */
  static verdict(
    dims: { w: number; h: number },
    stats: TitleLogoPixelStats,
    isPng: boolean,
  ): TitleLogoVerdict {
    if (!isPng) {
      return { block: 'The title logo must be a PNG with a transparent background.' };
    }
    if (!stats.hasAlpha) {
      return {
        block:
          'This PNG has no transparency. Export the logo with a transparent background (no photo or box behind the text).',
      };
    }
    const transparentRatio = stats.sampled > 0 ? stats.transparent / stats.sampled : 0;
    if (transparentRatio < TITLE_LOGO_MIN_TRANSPARENT_RATIO) {
      return {
        block: `Only ${Math.round(transparentRatio * 100)}% of this image is transparent — the title logo needs at least ${Math.round(TITLE_LOGO_MIN_TRANSPARENT_RATIO * 100)}%. Leave out any photo or box behind the text.`,
      };
    }
    if (stats.opaqueMedianLuminance === null) {
      return { block: 'This image has no visible lettering — the logo is fully transparent.' };
    }
    if (stats.opaqueMedianLuminance < TITLE_LOGO_MIN_OPAQUE_MEDIAN_LUMINANCE) {
      return {
        block: `This logo is too dark for the dark hero background (brightness ${Math.round(stats.opaqueMedianLuminance * 100)}%, needs ${Math.round(TITLE_LOGO_MIN_OPAQUE_MEDIAN_LUMINANCE * 100)}%). Use white or light colors with solid strokes.`,
      };
    }
    if (dims.w < TITLE_LOGO_MIN_W || dims.h < TITLE_LOGO_MIN_H) {
      return {
        block: `This image is ${dims.w} x ${dims.h}. The title logo needs at least ${TITLE_LOGO_MIN_W} x ${TITLE_LOGO_MIN_H}; ${TITLE_LOGO_W} x ${TITLE_LOGO_H} is recommended.`,
      };
    }
    const target = TITLE_LOGO_W / TITLE_LOGO_H;
    const ratio = dims.h > 0 ? dims.w / dims.h : 0;
    const offAspect = Math.abs(ratio - target) / target > TITLE_LOGO_ASPECT_TOLERANCE;
    const small = dims.w < TITLE_LOGO_W || dims.h < TITLE_LOGO_H;
    if (offAspect) {
      return {
        warn: `This image is ${dims.w} x ${dims.h} (${ratio.toFixed(2)}:1). It is scaled to fit ${TITLE_LOGO_W} x ${TITLE_LOGO_H} with transparent padding${small ? ' and will be scaled up, so it may look soft' : ''}; a 5:2 logo at ${TITLE_LOGO_W} x ${TITLE_LOGO_H} fills the title slot best.`,
      };
    }
    if (small) {
      return {
        warn: `This image is ${dims.w} x ${dims.h}, smaller than ${TITLE_LOGO_W} x ${TITLE_LOGO_H}. It will be scaled up and may look soft on a TV.`,
      };
    }
    return {};
  }

  /**
   * Fit the mark to the 1000 x 400 canvas, aspect preserved, scaling UP or
   * DOWN so it fills the canvas in one direction (the shells render the
   * delivered canvas at most at 0.4 scale — the 500 x 160 FHD box — so the
   * never-enlarge rule belongs to the shell's slot, not to this step; a
   * 500 x 200 source left unscaled would show at 200 x 80 on a TV).
   * Left-aligned and bottom-anchored: the shells draw the canvas in the
   * title's slot with its left edge at the content margin and its bottom
   * where the typed title's bottom sits (ADR-0013), so padding goes right and
   * up — never under or before the mark.
   */
  static fitPadGeometry(srcW: number, srcH: number): FitPadGeometry {
    if (srcW <= 0 || srcH <= 0) {
      return { scale: 1, drawW: 0, drawH: 0, dx: 0, dy: TITLE_LOGO_H };
    }
    const scale = Math.min(TITLE_LOGO_W / srcW, TITLE_LOGO_H / srcH);
    const drawW = Math.min(TITLE_LOGO_W, Math.round(srcW * scale));
    const drawH = Math.min(TITLE_LOGO_H, Math.round(srcH * scale));
    return { scale, drawW, drawH, dx: 0, dy: TITLE_LOGO_H - drawH };
  }

  /** True when the source already is the exact canvas — no redraw needed. */
  static isExactCanvas(srcW: number, srcH: number): boolean {
    return srcW === TITLE_LOGO_W && srcH === TITLE_LOGO_H;
  }

  /** `.png` filename for the processed upload, keeping the customer's base name. */
  static processedFilename(original: string): string {
    const base = original.replace(/\.[^.]+$/, '') || 'title-logo';
    return `${base}.png`;
  }

  static isPngFile(file: { type?: string; name: string }): boolean {
    if (file.type === 'image/png') return true;
    return /\.png$/i.test(file.name);
  }

  // ── Browser plumbing (thin; the math above is what the tests pin) ──

  /** Stats from a decoded bitmap through a canvas read-back. */
  static statsFromBitmap(bitmap: ImageBitmap): TitleLogoPixelStats {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { sampled: 0, transparent: 0, opaque: 0, hasAlpha: false, opaqueMedianLuminance: null };
    ctx.drawImage(bitmap, 0, 0);
    const data = ctx.getImageData(0, 0, bitmap.width, bitmap.height).data;
    return TitleLogoUtils.pixelStats(data, TitleLogoUtils.sampleStride(bitmap.width * bitmap.height));
  }

  /**
   * Fit-and-pad the bitmap into a 1000 x 400 transparent PNG File. Returns
   * the original file untouched when it already is the exact canvas.
   */
  static async fitPadToPng(file: File, bitmap: ImageBitmap): Promise<File> {
    if (TitleLogoUtils.isExactCanvas(bitmap.width, bitmap.height) && TitleLogoUtils.isPngFile(file)) {
      return file;
    }
    const g = TitleLogoUtils.fitPadGeometry(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = TITLE_LOGO_W;
    canvas.height = TITLE_LOGO_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare the title logo (no canvas support).');
    ctx.clearRect(0, 0, TITLE_LOGO_W, TITLE_LOGO_H);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, g.dx, g.dy, g.drawW, g.drawH);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not encode the title logo as PNG.');
    return new File([blob], TitleLogoUtils.processedFilename(file.name), { type: 'image/png' });
  }
}
